import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {useCallback, useState} from 'react';
import {QuestionStatus} from '@cp/model';
import {env} from '../../config/env';

export interface BulkChangeQuestionStatusDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (filterStatus: QuestionStatus, changeStatus: QuestionStatus) => void;
  loading?: boolean;
}

export default function BulkChangeQuestionStatusDialog({
  open,
  onClose,
  onSubmit,
  loading = false,
}: BulkChangeQuestionStatusDialogProps) {
  const [filterStatus, setFilterStatus] = useState<QuestionStatus | ''>('');
  const [changeStatus, setChangeStatus] = useState<QuestionStatus | ''>('');

  const handleSubmit = useCallback(() => {
    if (filterStatus && changeStatus && filterStatus !== changeStatus) {
      onSubmit(filterStatus as QuestionStatus, changeStatus as QuestionStatus);
    }
  }, [filterStatus, changeStatus, onSubmit]);

  const handleClose = useCallback(() => {
    setFilterStatus('');
    setChangeStatus('');
    onClose();
  }, [onClose]);

  const isValid = filterStatus && changeStatus && filterStatus !== changeStatus;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{fontWeight: 600, fontSize: '1.25rem'}}>Bulk Change Question Status</DialogTitle>
      <DialogContent>
        <Box sx={{pt: 1}}>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Change the status of all questions in this module that match the selected filter status.
            </Typography>

            <FormControl fullWidth>
              <InputLabel>From Status (Filter)</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as QuestionStatus)}
                label="From Status (Filter)"
              >
                {Object.values(QuestionStatus).map((statusValue) => (
                  <MenuItem key={statusValue} value={statusValue}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: env.question.status[statusValue]?.color || env.question.unknownStatus.color,
                        }}
                      />
                      {env.question.status[statusValue]?.label || env.question.unknownStatus.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>To Status (Change To)</InputLabel>
              <Select
                value={changeStatus}
                onChange={(e) => setChangeStatus(e.target.value as QuestionStatus)}
                label="To Status (Change To)"
              >
                {Object.values(QuestionStatus).map((statusValue) => (
                  <MenuItem key={statusValue} value={statusValue}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: env.question.status[statusValue]?.color || env.question.unknownStatus.color,
                        }}
                      />
                      {env.question.status[statusValue]?.label || env.question.unknownStatus.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {filterStatus && changeStatus && filterStatus === changeStatus && (
              <Typography variant="body2" color="error">
                Filter status and change status cannot be the same.
              </Typography>
            )}

            {filterStatus && changeStatus && filterStatus !== changeStatus && (
              <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                All questions with status &quot;{env.question.status[filterStatus]?.label || filterStatus}&quot; will be
                changed to &quot;{env.question.status[changeStatus]?.label || changeStatus}&quot;.
              </Typography>
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || loading}
          sx={{
            bgcolor: '#1B263B',
            '&:hover': {bgcolor: '#1B263B', opacity: 0.8},
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Create Job'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
